import path from 'path';
import { Prompt } from 'react-router-dom';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { editPermissionSelector, viewPermissionSelector } from '../common/classes/permissions';
import entityComposition from '../common/classes/entityComposition';
import routeHelper from '../common/classes/routeHelper';
import SectionPanel from '../common/containers/sectionPanel';
import AssetMaintenance from './assetMaintenance';
import Associations from './assetAssociations';
import AssetContainer from './assetContainer';
import AssetCoatings from './assetCoatings';
import AssetComments from './assetComments';
import AssetGeneral from './assetGeneral';
import AssetDetails from './assetDetails';
import AssetProfile from './assetProfile';
import AssetPhotos from './assetPhotos';
class Asset extends Component {
  constructor(props) {
    if (!props.match.params || !props.match.params.id) {
      throw new Error('props.match.params.id is required');
    }
    super(props);
    this.onBack = routeHelper.updateURL.bind(routeHelper, '/assets');
    this.handleSave = this.handleSave.bind(this);
    this.validate = this.validate.bind(this);
  }
  componentDidMount() {
    const {
      asset: {
        active: {
          isNew
        }
      },
      match: {
        params: {
          id
        }
      },
      assetActions: {
        serverShow
      }
    } = this.props;
    if (!isNew) {
      serverShow(id);
    }
  }
  componentWillUnmount() {
    const {
      assetActions: {
        showCancel
      }
    } = this.props;
    showCancel();
  }
  validate() {
    const errors = [];
    const {
      asset: {
        active
      }
    } = this.props;
    const {
      categoryName,
      typeName,
      assetTypeID,
      assetTypeOtherID,
      organizations
    } = active;
    if (categoryName) {
      if (!typeName && !assetTypeID && !assetTypeOtherID) {
        errors.push('If you have an asset category selected, the asset type is a required field.');
      }
    }
    if (organizations && organizations.length) {
      let organizationError = false;
      let facilityError = false;
      let groupError = false;
      let areaError = false;
      organizations.forEach(organization => {
        if (!organization.id) {
          organizationError = true;
        }
        if (organization.facilities) {
          organization.facilities.forEach(facility => {
            if (!facility.id) {
              facilityError = true;
            }
            if (facility.groups) {
              facility.groups.forEach(group => {
                if (!group.id) {
                  groupError = true;
                }
                if (group.areas) {
                  group.areas.forEach(area => {
                    if (!area.id) {
                      areaError = true;
                    }
                  });
                }
              });
            }
          });
        }
      });
      if (organizationError) {
        errors.push('If you have associated organizations, all organizations are required.');
      }
      if (facilityError) {
        errors.push('If you have associated facilities, all facilities are required.');
      }
      if (groupError) {
        errors.push('If you have associated groups, all groups are required.');
      }
      if (areaError) {
        errors.push('If you have associated areas, all areas are required.');
      }
    }
    return errors;
  }
  handleSave() {
    const {
      asset,
      assetActions
    } = this.props;
    const errors = this.validate();
    const {
      active
    } = asset;
    if (errors.length > 0) {
      assetActions.errorMessage(errors);
    } else {
      assetActions.serverSave(active);
    }
  }
  render() {
    const {
      match: {
        params: {
          section = 'general',
          id
        }
      },
      asset: {
        pendingChanges,
        loading,
        active
      },
      assetActions,
      userID,
      canEdit
    } = this.props;
    const props = {
      asset: active,
      assetActions,
      loading,
      userID
    };
    const {
      name
    } = active;
    const forms = {
      general: /*#__PURE__*/React.createElement(AssetGeneral, props),
      associations: /*#__PURE__*/React.createElement(Associations, props),
      coatingDetails: /*#__PURE__*/React.createElement(AssetCoatings, props),
      detailDescriptions: /*#__PURE__*/React.createElement(AssetDetails, props),
      profile: /*#__PURE__*/React.createElement(AssetProfile, props),
      maintenance: /*#__PURE__*/React.createElement(AssetMaintenance, props),
      comments: /*#__PURE__*/React.createElement(AssetComments, props),
      photos: /*#__PURE__*/React.createElement(AssetPhotos, props)
    };
    const sectionRoutes = Object.keys(forms).map(formKey => path.join('/assets/', id, formKey));
    return /*#__PURE__*/React.createElement(AssetContainer, {
      section: section,
      id: id
    }, /*#__PURE__*/React.createElement(Prompt, {
      when: pendingChanges,
      message: location => routeHelper.transitionAsync(this, 'asset', location, sectionRoutes)
    }), /*#__PURE__*/React.createElement(SectionPanel, {
      isSaveDisabled: loading || !pendingChanges,
      isPending: pendingChanges,
      onBack: this.onBack,
      onSave: this.handleSave,
      name: name,
      showSave: canEdit
    }, section in forms ? forms[section] : null));
  }
}
Asset.defaultProps = {
  asset: {
    active: {}
  },
  assetActions: {}
};
Asset.propTypes = {
  assetActions: PropTypes.shape({
    change: PropTypes.func,
    serverSave: PropTypes.func,
    serverShow: PropTypes.func,
    showCancel: PropTypes.func,
    errorMessage: PropTypes.func
  }),
  asset: PropTypes.shape({
    active: PropTypes.shape({
      isNew: PropTypes.bool,
      id: PropTypes.string,
      name: PropTypes.string,
      categoryName: PropTypes.string,
      typeName: PropTypes.string,
      assetTypeID: PropTypes.string,
      assetTypeOtherID: PropTypes.string,
      organizations: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        facilities: PropTypes.shape({
          id: PropTypes.string,
          groups: PropTypes.shape({
            id: PropTypes.string,
            areas: PropTypes.shape({
              id: PropTypes.string
            })
          })
        })
      }))
    }),
    pendingChanges: PropTypes.bool.isRequired,
    loading: PropTypes.bool.isRequired
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
      section: PropTypes.string
    }).isRequired
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
  canView: PropTypes.bool.isRequired,
  userID: PropTypes.string.isRequired
};
export { Asset as TestAsset };
export default entityComposition(Asset, ['asset'], {
  canView: state => viewPermissionSelector('asset')(state),
  canEdit: state => editPermissionSelector('asset')(state),
  userID: state => state.login.user.user_id
});